// Types
import type { CourseData } from "@/api/reports";

interface ProgressStatusEntry {
  icon: string;
  color: string;
}

interface AssignmentStatusEntry {
  icon: string;
  color: string;
}

interface LevelsData {
  courses?: CourseData[];
  coursesByRank?: Record<string, CourseData>;
}

type SelectOption = {
  label: string;
  value: string;
};

type ProgressStatusKey = "completed" | "in_progress" | "not_started" | "paused";
type AssignmentStatusKey = "completed" | "overdue" | "inProgress" | "paused" | "notStarted";

/** Raw status value returned by the assignments report API. */
export type AssignmentRowStatus = "completed" | "overdue" | "in progress" | "paused";

/** Status values accepted by the assignments report filter params. */
export type AssignmentStatusFilter =
  | "completed"
  | "overdue"
  | "in-progress"
  | "paused"
  | "not_started";

// Progress status configuration using status field
export const PROGRESS_STATUS_CONFIG: Record<ProgressStatusKey, ProgressStatusEntry> = {
  completed: { icon: "pi-check-circle", color: "" },
  in_progress: { icon: "pi-spin pi-spinner-dotted", color: "" },
  not_started: { icon: "", color: "" },
  paused: { icon: "pi-pause-circle", color: "" },
};

export const transformCoursesToRankMap = (
  courses: CourseData[] = []
): Record<string, CourseData> => {
  const rankMap: Record<string, CourseData> = {};

  courses.forEach(course => {
    if (course.rankName && course.name) {
      rankMap[course.rankName] = {
        name: course.name,
        rankName: course.rankName,
      };
    }
  });

  return rankMap;
};

// Progress level filter options
export const getProgressLevelOptions = (levelsData: LevelsData = {}): SelectOption[] => {
  let coursesByRank: Record<string, CourseData> = {};

  if (levelsData.courses && Array.isArray(levelsData.courses)) {
    coursesByRank = transformCoursesToRankMap(levelsData.courses);
  } else if (levelsData.coursesByRank) {
    coursesByRank = levelsData.coursesByRank;
  }

  // Get all available ranks from the data
  const availableRanks = Object.keys(coursesByRank);

  // Generate options dynamically for all available ranks
  return availableRanks.map(rank => ({
    label: (() => {
      const courseData = coursesByRank[rank];
      // Use course name if available and not empty, otherwise use rank name
      return courseData && courseData.name && courseData.name.trim().length > 0
        ? courseData.name
        : rank.toUpperCase(); // Fallback to rank name if no course name
    })(),
    value: rank,
  }));
};

// Progress category filter options
export const PROGRESS_CATEGORY_OPTIONS: SelectOption[] = [
  { label: "Course", value: "course" },
  { label: "Quest", value: "quest" },
];

// Progress status filter options for multiSelect
export const PROGRESS_STATUS_OPTIONS: SelectOption[] = [
  { label: "Not Started", value: "not_started" },
  { label: "In Progress", value: "in_progress" },
  { label: "Completed", value: "passed" },
  { label: "Paused", value: "abandoned" },
];

// Progress unenrolled filter options
export const PROGRESS_UNENROLLED_OPTIONS: SelectOption[] = [
  { label: "Show users who are not enrolled in any visible paths", value: "true" },
];

// Progress archived filter options
export const PROGRESS_ARCHIVED_OPTIONS: SelectOption[] = [
  { label: "Include", value: "true" },
  { label: "Exclusively Users", value: "userOnly" },
  { label: "Exclusively Paths", value: "pathOnly" },
];

// Helper function to determine progress status
export const getProgressStatusKey = (status?: string | null): ProgressStatusKey => {
  if (!status) return "not_started";

  switch (status.toLowerCase()) {
    case "completed":
      return "completed";
    case "in progress":
      return "in_progress";
    case "not started":
      return "not_started";
    case "passed":
      return "completed";
    case "abandoned":
      return "paused";
    case "paused":
      return "paused";
    default:
      return "not_started";
  }
};

// Assignment status configuration using completed_at and status fields
export const ASSIGNMENT_STATUS_CONFIG: Record<AssignmentStatusKey, AssignmentStatusEntry> = {
  completed: { icon: "pi-check-circle", color: "" },
  overdue: { icon: "pi-exclamation-circle", color: "warning" },
  inProgress: { icon: "pi-spin pi-spinner-dotted", color: "" },
  paused: { icon: "pi-pause-circle", color: "" },
  notStarted: { icon: "", color: "" },
};

// Status filter options for multiSelect
export const ASSIGNMENT_STATUS_OPTIONS: SelectOption[] = [
  { label: "Completed", value: "completed" },
  { label: "Overdue", value: "overdue" },
  { label: "In Progress", value: "in-progress" },
  { label: "Paused", value: "paused" },
  { label: "Not Started", value: "not_started" },
];

// Helper function to determine assignment status
export const getAssignmentStatusKey = (rowData: {
  status?: AssignmentRowStatus | null;
}): AssignmentStatusKey => {
  const status = rowData.status;
  if (!status) return "notStarted";

  switch (status.toLowerCase()) {
    case "completed":
      return "completed";
    case "overdue":
      return "overdue";
    case "in progress":
      return "inProgress";
    case "paused":
      return "paused";
    default:
      return "notStarted";
  }
};
